import type { AxiosError } from 'axios'
import { filter, get, groupBy, map } from 'lodash'
import type { FlowTypes } from 'v2/types'
import { logger } from 'v2/utils/logger'
import type { ZodCustomIssue, ZodError } from 'zod'
import { nodesSchema } from '../config/validation.config'
import { FlowNodeTypes, type GenericEdge, type GenericNode } from '../types'
import { isAiAgentOrActionNode } from './ai-agent-utils'
import { transformFlowData } from './data-transformer'
import { isPendingEdgeHandle } from './pending-edge'
import { processNodeIssues } from './validation-error-processor'

// Intent names reserved for runtime output states across the AI-node runtimes
// (console, QES, livekit each carry a subset). An author-defined intent named
// after one of these would route call-terminal semantics through a user port.
export const SNAKE_CASE_VARIABLE_NAME = /^[a-z][a-z0-9_]*$/

const RESERVED_INTENT_NAMES = new Set(
  [
    'conversation_complete',
    'human_handoff',
    'error',
    'Hold Requested',
    'Voicemail Detected',
    'inequipped_to_serve',
  ].map((name) => name.toLowerCase()),
)

// Custom error class to include node information for validation errors
export class NodeValidationError extends Error {
  public nodeId: string

  public nodeName: string

  public path?: string[]

  constructor(message: string, nodeId: string, nodeName: string, path?: string[]) {
    super(message)
    this.name = 'NodeValidationError'
    this.nodeId = nodeId
    this.nodeName = nodeName
    this.path = path
  }
}

export class MultipleNodeValidationError extends Error {
  public issues: NodeValidationError[]

  constructor(issues: NodeValidationError[]) {
    super(`Validation failed for ${issues.length} node(s).`)
    this.name = 'MultipleNodeValidationError'
    this.issues = issues
  }
}
interface EdgeIssue {
  edgeId: string
  issue: ZodCustomIssue
}

interface ValidationResult {
  isValid: boolean
  error?: { title: string; description: string }
  issuesToUpdate?: ZodCustomIssue[]
  edgeIssuesToUpdate?: EdgeIssue[]
}

// Helper function to create a ZodCustomIssue from backend validation error
const createBackendValidationIssue = (
  nodeId: string,
  field: string,
  message: string,
): ZodCustomIssue => ({
  code: 'custom',
  message,
  path: ['data', 'config', 'model', field],
  params: { data: { id: nodeId } },
})

export const handleBackendValidationErrors = (error: unknown, data: unknown): ZodCustomIssue[] => {
  const backendValidationIssues: ZodCustomIssue[] = []

  // Validate input parameters
  if (!data || typeof data !== 'object') {
    return backendValidationIssues
  }

  if (!error || typeof error !== 'object' || !('response' in error)) {
    return backendValidationIssues
  }

  const axiosError = error as AxiosError
  const responseData = axiosError.response?.data?.data

  // Check if the response contains validation errors in the expected format
  if (!responseData?.nodes || typeof responseData.nodes !== 'object') {
    return backendValidationIssues
  }

  // Process each node error using the node ID directly
  Object.entries(responseData.nodes).forEach(([nodeId, nodeIssues]) => {
    const issues = processNodeIssues(nodeIssues, nodeId, {
      handleDirectError: (currentNodeId, message) =>
        createBackendValidationIssue(currentNodeId, 'general', message),
      handleConfigError: (currentNodeId, field, message) =>
        createBackendValidationIssue(currentNodeId, field, message),
    })
    backendValidationIssues.push(...issues)
  })

  return backendValidationIssues
}

export const validateFlowContentForUi = (
  nodes: GenericNode[],
  edges: GenericEdge[],
  flowType: FlowTypes | undefined,
  allowEmptyConnection: boolean,
  actionContext: 'publish' | 'save' | 'download',
  isLivekitEnabled?: boolean,
): ValidationResult => {
  try {
    nodesSchema({ edges }).parse(nodes)
  } catch (e: unknown) {
    const issues = get(e as ZodError, 'issues', []) as ZodCustomIssue[]
    const issueMessages = map(issues, 'message')
    logger.debug(
      '[validateFlowContentForUi] Validation Error Context:',
      actionContext,
      'Issue Messages:',
      issueMessages,
    )

    let title = ''
    if (actionContext === 'publish') title = 'Unable to publish'
    else if (actionContext === 'save') title = 'Unable to save'
    else if (actionContext === 'download') title = 'Unable to download'
    else title = 'Validation Error'

    return {
      isValid: false,
      error: {
        title,
        description: issueMessages.join('\n'),
      },
      issuesToUpdate: issues,
    }
  }

  // --- Edge validation for duplicate intent names in AI nodes ---
  const aiNodes = nodes.filter((node) => isAiAgentOrActionNode(node.type))
  if (aiNodes.length === 0) {
    // Skip edge validation if no AI nodes exist
    if (!allowEmptyConnection) {
      const { data: transformedData } = transformFlowData({ nodes, edges }, flowType as FlowTypes)
      if (transformedData.connections.length === 0) {
        return {
          isValid: false,
          error: {
            title: 'Invalid Agent Flow',
            description:
              'An agent flow needs at least one node connected to the Start node to be saved. Please add a node.',
          },
        }
      }
    }
  }

  const edgeIssuesToUpdate: EdgeIssue[] = []

  aiNodes.forEach((node) => {
    const intents = get(node, 'data.config.intents', [])
    if (!Array.isArray(intents) || intents.length < 2) return

    const grouped = groupBy(intents, 'intent_name')
    const duplicates = Object.entries(grouped).filter(([, intentArray]) => intentArray.length > 1)

    if (duplicates.length === 0) return

    const outgoingEdges = filter(edges, (e) => e.source === node.id)
    outgoingEdges.forEach((edge) => {
      edgeIssuesToUpdate.push({
        edgeId: edge.id,
        issue: {
          code: 'custom',
          message: 'Duplicate intent names found in AI node.',
          path: ['data', 'config', 'intents'],
          params: { nodeId: node.id, edgeId: edge.id },
        },
      })
    })
  })

  if (edgeIssuesToUpdate.length)
    return {
      isValid: false,
      edgeIssuesToUpdate,
      error: {
        title: 'Duplicate Intents',
        description:
          'Two or more intents in an AI node have the same name. Please ensure all intent names are unique.',
      },
    }

  // --- Edge validation for default-edge source handles ---
  const defaultEdgeIssues: EdgeIssue[] = []

  edges.forEach((edge) => {
    if (isPendingEdgeHandle(edge.sourceHandle)) {
      defaultEdgeIssues.push({
        edgeId: edge.id,
        issue: {
          code: 'custom',
          message: 'Edge has default source handle and needs to be configured.',
          path: ['sourceHandle'],
          params: { edgeId: edge.id },
        },
      })
    }
  })

  if (defaultEdgeIssues.length)
    return {
      isValid: false,
      edgeIssuesToUpdate: defaultEdgeIssues,
      error: {
        title: 'Unconfigured Edges',
        description:
          'Some edges have no path selected. Please click on the edge and select a path from the dropdown menu.',
      },
    }

  // --- agent_node publish-time checks (drafts pass) ---
  if (actionContext === 'publish') {
    const agentNodes = nodes.filter((node) => node.type === FlowNodeTypes.agent_node)

    // A Collect input node is livekit-backed; publishing one needs the org LiveKit
    // capability. Only enforced when the caller passed the flag explicitly.
    if (agentNodes.length > 0 && isLivekitEnabled === false) {
      return {
        isValid: false,
        error: {
          title: 'Unable to publish',
          description:
            'This flow uses a Collect input node, which needs LiveKit enabled for your organization. Contact support to enable it.',
        },
      }
    }

    for (const node of agentNodes) {
      const intents = get(node, 'data.config.intents', []) as Array<{ intent_name?: string }>

      if (!Array.isArray(intents) || intents.length === 0) {
        return {
          isValid: false,
          error: {
            title: 'Unable to publish',
            description: `The Collect input node "${get(node, 'data.meta.name', node.id)}" needs at least one intent before it can be published.`,
          },
        }
      }

      const reserved = intents.find(
        (intent) =>
          intent.intent_name && RESERVED_INTENT_NAMES.has(intent.intent_name.trim().toLowerCase()),
      )
      if (reserved) {
        return {
          isValid: false,
          error: {
            title: 'Unable to publish',
            description: `The intent name "${reserved.intent_name}" is reserved. Rename it in the Collect input node "${get(node, 'data.meta.name', node.id)}".`,
          },
        }
      }

      // Variable names are snake_case (imports can carry anything; the panel
      // sanitizes as you type, this is the backstop).
      const agentTasks = get(node, 'data.config.agent_tasks', {}) as {
        variables?: Array<{ name?: string }>
        extract_only?: Array<{ name?: string }>
      }
      const badName = [...(agentTasks.variables ?? []), ...(agentTasks.extract_only ?? [])].find(
        (row) => !row.name || !SNAKE_CASE_VARIABLE_NAME.test(row.name),
      )
      if (badName) {
        return {
          isValid: false,
          error: {
            title: 'Unable to publish',
            description: `The variable name "${badName.name ?? ''}" in the Collect input node "${get(node, 'data.meta.name', node.id)}" must use lowercase letters, numbers, and underscores.`,
          },
        }
      }
    }
  }

  return { isValid: true }
}
